<template>
  <NuxtLink
    :to="professionTo"
    class="profession-card">
    <!-- ── IMAGE DE FOND ─────────────────────────────────────────────── -->
    <div class="profession-card__image">
      <img
        v-if="props.profession.BackgroundImage"
        :src="props.profession.BackgroundImage"
        :alt="props.profession.Name"
        class="profession-card__img" />
    </div>

    <!-- ── PANNEAU CONTENU (s'agrandit au hover) ─────────────────────────── -->
    <div class="profession-card__panel">
      <!-- Titre toujours visible -->
      <div class="profession-card__header">
        <h3 class="profession-card__title">{{ props.profession.Name }}</h3>
      </div>

      <!-- Description - révélée au hover -->
      <div class="profession-card__description">
        <p class="profession-card__desc-text">{{ props.profession.Description }}</p>
      </div>

      <!-- Footer avec infos et CTA -->
      <div class="profession-card__footer">
        <!-- Domaine (univers professionnel large) -->
        <div
          v-if="props.profession.Domain"
          class="profession-card__meta">
          <svg
            class="profession-card__icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true">
            <rect
              x="3"
              y="3"
              width="8"
              height="8"
              rx="1.5"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linejoin="round" />
            <rect
              x="13"
              y="3"
              width="8"
              height="8"
              rx="1.5"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linejoin="round" />
            <rect
              x="3"
              y="13"
              width="8"
              height="8"
              rx="1.5"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linejoin="round" />
            <rect
              x="13"
              y="13"
              width="8"
              height="8"
              rx="1.5"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linejoin="round" />
          </svg>
          <span class="profession-card__category">{{ props.profession.Domain }}</span>
        </div>

        <!-- Secteur (entreprise / industrie) -->
        <div
          v-if="props.profession.Sector"
          class="profession-card__meta">
          <svg
            class="profession-card__icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true">
            <path
              d="M3 21V8C3 7.45 3.45 7 4 7H11C11.55 7 12 7.45 12 8V21"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round" />
            <path
              d="M12 21V12C12 11.45 12.45 11 13 11H20C20.55 11 21 11.45 21 12V21"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round" />
            <path
              d="M2 21H22"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round" />
            <path
              d="M6 11H6.01M9 11H9.01M6 15H6.01M9 15H9.01M15 15H15.01M18 15H18.01M15 18H15.01M18 18H18.01"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round" />
          </svg>
          <span class="profession-card__category">{{ props.profession.Sector }}</span>
        </div>

        <!-- Salaire -->
        <div
          v-if="props.profession.Salary"
          class="profession-card__meta">
          <svg
            class="profession-card__icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true">
            <rect
              x="2"
              y="6"
              width="20"
              height="12"
              rx="2"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linejoin="round" />
            <circle
              cx="12"
              cy="12"
              r="2.5"
              stroke="currentColor"
              stroke-width="1.8" />
            <path
              d="M6 9.5V9.51M18 14.5V14.51"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round" />
          </svg>
          <span class="profession-card__category">{{ props.profession.Salary }}</span>
        </div>
        <!-- CTA "Découvrir le métier" -->
        <span class="profession-card__cta">
          Découvrir le métier <span class="profession-card__arrow">›</span>
        </span>
      </div>
    </div>
  </NuxtLink>
</template>

<script lang="ts" setup>
import type { ComputedRef, PropType } from 'vue'
import type { ProfessionCardObj } from '~/types/profession'

interface ProfessionCardProps {
  profession: ProfessionCardObj
}

/**
 * Définit les props du composant ProfessionCard.
 */
const props: ProfessionCardProps = defineProps({
  profession: {
    type: Object as PropType<ProfessionCardObj>,
    required: true,
  },
})

const localePath: ReturnType<typeof useLocalePath> = useLocalePath()

const professionTo: ComputedRef<string> = computed<string>(() => {
  const link: string = (props.profession.Link ?? '').trim()
  return localePath(link.length > 0 ? link : '/metiers')
})
</script>

<style scoped>
/* ════════════════════════════════════════════════════════════
   PROFESSION CARD COMPONENT
   ════════════════════════════════════════════════════════════ */

.profession-card {
  --card-image-height: 220px;
  --card-panel-min-height: 240px;
  --card-desc-max-height: 9.375rem;
  --card-title-lines: 2.75rem;

  position: relative;
  display: block;
  height: 100%;
  min-height: calc(var(--card-image-height) + var(--card-panel-min-height));
  border-radius: var(--radius-lg);
  overflow: hidden;
  text-decoration: none;
  transition:
    border-color var(--duration-base) var(--ease-out),
    box-shadow var(--duration-base) var(--ease-out),
    transform var(--duration-base) var(--ease-out);
  border: 1px solid var(--border-subtle);
}

/* prettier-ignore */
.profession-card:hover {
  border-color: var(--accent-orange);
  box-shadow: var(--shadow-lg), 0 0 20px rgba(249, 115, 22, 0.25);
  transform: translateY(-4px);
}

/* ── IMAGE (fixe en haut, recouverte par le panneau au hover) ────────── */
.profession-card__image {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 0;
  height: var(--card-image-height);
  overflow: hidden;
  background: linear-gradient(135deg, rgba(3, 160, 241, 0.1) 0%, rgba(249, 115, 22, 0.05) 100%);
}

.profession-card__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform var(--duration-base) var(--ease-out);
}

/* ── PANNEAU (bas de carte, monte au hover sans agrandir la carte) ───── */
.profession-card__panel {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: var(--card-panel-min-height);
  max-height: var(--card-panel-min-height);
  padding: var(--space-5) var(--space-6) var(--space-6);
  overflow: hidden;
  background: var(--bg-surface);
}

.profession-card:hover .profession-card__panel {
  height: auto;
  max-height: 100%;
}

/* ── HEADER (titre — hauteur réservée pour 2 lignes) ───────────────── */
.profession-card__header {
  flex-shrink: 0;
  min-height: var(--card-title-lines);
}

.profession-card__title {
  font-size: var(--text-lg);
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: var(--leading-snug);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  transition: color var(--duration-fast) var(--ease-out);
}

.profession-card:hover .profession-card__title {
  color: var(--accent-orange);
}

/* ── DESCRIPTION (max-height 0 → scrollable, anime la hauteur du panneau) ─── */
.profession-card__description {
  box-sizing: border-box;
  max-height: 0;
  margin-top: var(--space-1);
  margin-bottom: var(--space-5);
  padding-top: 0;
  overflow: auto;
  transition: all 0.4s ease;
}

.profession-card:hover .profession-card__description {
  max-height: var(--card-desc-max-height);
  padding-top: var(--space-5);
  -webkit-overflow-scrolling: touch;
}

.profession-card__desc-text {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  margin: 0;
  line-height: var(--leading-normal);
}

/* ── FOOTER (infos + CTA) ─────────────────────────────────────────────── */
.profession-card__footer {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  flex-shrink: 0;
  margin-top: auto;
  padding-top: var(--space-4);
  border-top: 1px solid var(--border-subtle);
}

.profession-card__meta {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.profession-card__icon {
  flex-shrink: 0;
  color: var(--accent-orange);
  opacity: 0.85;
}

.profession-card__category {
  opacity: 0.85;
}

.profession-card__cta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-2);
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-secondary);
  transition: all var(--duration-fast) var(--ease-out);
}

.profession-card:hover .profession-card__cta {
  color: var(--accent-orange);
}

.profession-card__arrow {
  display: inline-block;
  transition: transform var(--duration-fast) var(--ease-out);
}

.profession-card:hover .profession-card__arrow {
  transform: translateX(4px);
}

/* ── RESPONSIVE ─────────────────────────────────────────────── */
@media (max-width: 768px) {
  .profession-card {
    --card-image-height: 180px;
    --card-panel-min-height: 220px;
    --card-title-lines: 2.5rem;
  }

  .profession-card__title {
    font-size: var(--text-base);
  }

  .profession-card__desc-text {
    font-size: var(--text-xs);
  }

  .profession-card__panel {
    height: auto;
    max-height: 100%;
  }

  .profession-card__description {
    max-height: var(--card-desc-max-height);
    padding-top: var(--space-5);
    -webkit-overflow-scrolling: touch;
  }

  .profession-card__footer {
    padding-top: var(--space-3);
  }
}

@media (max-width: 640px) {
  .profession-card {
    --card-image-height: 160px;
    --card-panel-min-height: 200px;
    --card-title-lines: 2.25rem;
  }

  .profession-card__title {
    font-size: var(--text-sm);
  }

  .profession-card__cta {
    font-size: var(--text-xs);
  }
}
</style>
